import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../components/AuthView';
import { CLOUD_API_URL } from '../config/serverConfig';
import { isSessionDead } from '../lib/sessionDead';

const CHECK_INTERVAL_MS = 45_000;
const IDLE_THRESHOLD_S = 30;
const COOLDOWN_MS = 120_000;

function pageFromPath(pathname) {
  const p = pathname.replace('/app', '').replace(/^\//, '');
  if (!p || p === '/') return 'home';
  if (p.startsWith('artist/')) return 'artist';
  if (p.startsWith('album/')) return 'album';
  if (p.startsWith('search')) return 'search';
  if (p.startsWith('genre')) return 'genres';
  if (p.startsWith('valet')) return 'valet';
  return p.split('/')[0] || 'home';
}

let recentSearches = [];
let emptyResults = [];
let repeatActions = [];

/**
 * Record a search that returned results (called from SearchPage).
 */
export function recordSearch(query) {
  const q = String(query || '').trim();
  if (!q) return;
  recentSearches.push(q);
  if (recentSearches.length > 10) recentSearches.shift();
}

/**
 * Record a search that returned zero results.
 */
export function recordEmptySearch(query) {
  const q = String(query || '').trim();
  if (!q) return;
  emptyResults.push(q);
  if (emptyResults.length > 5) emptyResults.shift();
}

/**
 * Record a repeated action pattern (e.g., viewing multiple artists).
 */
export function recordAction(type, name) {
  repeatActions.push({ type, name, ts: Date.now() });
  const fiveMinAgo = Date.now() - 5 * 60_000;
  repeatActions = repeatActions.filter(a => a.ts > fiveMinAgo);
  if (repeatActions.length > 20) repeatActions = repeatActions.slice(-20);
}

/**
 * Hook that detects frustration signals and fetches proactive suggestions.
 * Mount once in AppLayout.
 * Returns: { suggestion, dismissSuggestion }
 */
export default function useProactiveAssist() {
  const auth = useAuth();
  const location = useLocation();
  const [suggestion, setSuggestion] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const lastCheckRef = useRef(0);
  const cooldownUntilRef = useRef(0);

  // Track user activity to compute idle time
  useEffect(() => {
    const markActive = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('click', markActive, { passive: true });
    window.addEventListener('keydown', markActive, { passive: true });
    window.addEventListener('scroll', markActive, { passive: true });
    window.addEventListener('touchstart', markActive, { passive: true });
    return () => {
      window.removeEventListener('click', markActive);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('scroll', markActive);
      window.removeEventListener('touchstart', markActive);
    };
  }, []);

  // Periodic check
  useEffect(() => {
    if (!auth?.accessToken) return;

    const interval = setInterval(async () => {
      if (isSessionDead()) return;
      const now = Date.now();
      if (now < cooldownUntilRef.current) return;
      if (now - lastCheckRef.current < CHECK_INTERVAL_MS) return;

      // Only check if there's something to report
      const idleSeconds = Math.floor((now - lastActivityRef.current) / 1000);
      const hasSignals = emptyResults.length > 0 || repeatActions.length >= 3 || idleSeconds >= IDLE_THRESHOLD_S;
      if (!hasSignals) return;

      lastCheckRef.current = now;

      try {
        const res = await fetch(`${CLOUD_API_URL}/api/valet/assist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.accessToken}`,
          },
          credentials: 'omit',
          body: JSON.stringify({
            recentSearches: recentSearches.slice(-5),
            emptyResults: emptyResults.slice(-3),
            currentPage: pageFromPath(location.pathname),
            idleSeconds,
            repeatActions: repeatActions.slice(-10),
          }),
        });

        if (res.ok) {
          const data = await res.json();
          if (data?.suggestion) {
            setSuggestion(data.suggestion);
            // Clear the signals that triggered this suggestion
            emptyResults = [];
          }
        }
      } catch {
        // Silent fail — assist is non-critical
      }
    }, 10_000);

    return () => clearInterval(interval);
  }, [auth?.accessToken, location.pathname]);

  const dismissSuggestion = useCallback(() => {
    setSuggestion(null);
    cooldownUntilRef.current = Date.now() + COOLDOWN_MS;
  }, []);

  return { suggestion, dismissSuggestion };
}
